import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { openWhatsApp, PHONE_DISPLAY, EMAIL, ADDRESS } from "../lib/whatsapp";

const Section = ({ title, children }) => (
    <section className="mt-12">
        <h2 style={{fontFamily: "'Outfit', sans-serif"}} className="text-2xl md:text-3xl font-bold text-rvs-navy tracking-tight">
            {title}
        </h2>
        <div className="mt-5 space-y-4 text-[15px] md:text-base text-[#4A5568] leading-relaxed">
            {children}
        </div>
    </section>
);

export default function Terms() {
    return (
        <div data-testid="page-terms" className="bg-rvs-cream">
            <article className="max-w-3xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24">
                <Link to="/" data-testid="terms-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-rvs-navy/70 hover:text-rvs-orange link-underline">
                    <ArrowLeft className="w-4 h-4" /> Back to Home
                </Link>

                <p className="eyebrow mt-10">Legal · RVS Electronics</p>
                <h1 style={{fontFamily: "'Outfit', sans-serif"}} className="mt-4 text-5xl sm:text-6xl font-black tracking-tighter leading-[1.02] text-rvs-navy">
                    Terms &amp; Conditions
                </h1>
                <p className="mt-4 text-sm uppercase tracking-[0.2em] text-rvs-orange font-bold">
                    Effective Date: 10 May 2026
                </p>

                <p className="mt-10 text-base md:text-lg text-[#4A5568] leading-relaxed">
                    Welcome to the website of RVS Electronics.
                </p>
                <p className="mt-3 text-base md:text-lg text-[#4A5568] leading-relaxed">
                    By accessing or using this website, you agree to the following Terms &amp; Conditions.
                </p>

                <Section title="Business Information">
                    <p>
                        RVS Electronics is an authorized distributor of Sun Direct serving Armoor, Nizamabad District, Telangana and surrounding operational areas.
                    </p>
                </Section>

                <Section title="Purpose of Website">
                    <p>This website is intended to provide information regarding:</p>
                    <ul className="list-disc pl-6 space-y-1.5 marker:text-rvs-orange">
                        <li>DTH sales</li>
                        <li>new connection support</li>
                        <li>installation coordination</li>
                        <li>service support</li>
                        <li>recharge assistance</li>
                        <li>retailer support</li>
                        <li>business communication</li>
                        <li>upcoming digital initiatives including PAYCHEY</li>
                    </ul>
                    <p>The website is informational in nature unless otherwise stated.</p>
                </Section>

                <Section title="Service Availability">
                    <p>Services may be subject to:</p>
                    <ul className="list-disc pl-6 space-y-1.5 marker:text-rvs-orange">
                        <li>service area availability</li>
                        <li>operational feasibility</li>
                        <li>technician availability</li>
                        <li>customer location</li>
                        <li>company policies</li>
                        <li>material availability</li>
                    </ul>
                    <p>Service timelines may vary depending on operational conditions.</p>
                </Section>

                <Section title="Customer Communication">
                    <p>By contacting RVS Electronics through phone, WhatsApp, email, or website enquiry forms, you agree that we may contact you for:</p>
                    <ul className="list-disc pl-6 space-y-1.5 marker:text-rvs-orange">
                        <li>enquiry response</li>
                        <li>service coordination</li>
                        <li>installation scheduling</li>
                        <li>customer support</li>
                        <li>retailer support</li>
                        <li>service-related follow-up</li>
                    </ul>
                </Section>

                <Section title="Accuracy of Information">
                    <p>We make reasonable efforts to keep website information accurate and updated.</p>
                    <p>However, we do not guarantee that all information, offers, service availability, pricing, or operational details will always be current, complete, or error-free.</p>
                    <p>Information may change without prior notice.</p>
                </Section>

                <Section title="Third-Party Platforms">
                    <p>The website may refer to third-party services, platforms, or brands, including future digital payment or recharge systems.</p>
                    <p>Use of such services may be subject to separate terms and policies of those respective service providers.</p>
                </Section>

                <Section title="Intellectual Property">
                    <p>All content on this website, including text, layout, branding elements, graphics, and business information, belongs to RVS Electronics unless otherwise stated.</p>
                    <p>Unauthorized copying, reproduction, or commercial use is prohibited.</p>
                </Section>

                <Section title="Limitation of Liability">
                    <p>RVS Electronics shall not be liable for direct, indirect, incidental, or consequential loss arising from:</p>
                    <ul className="list-disc pl-6 space-y-1.5 marker:text-rvs-orange">
                        <li>use of this website</li>
                        <li>inability to access the website</li>
                        <li>reliance on website information</li>
                        <li>temporary interruptions</li>
                        <li>third-party services or external links</li>
                    </ul>
                </Section>

                <Section title="Future Digital Services">
                    <p>RVS Electronics may introduce future digital services, including PAYCHEY, retailer portals, recharge systems, and payment integrations.</p>
                    <p>Additional terms, privacy policies, and transaction-specific conditions may apply to those services when launched.</p>
                </Section>

                <Section title="Governing Jurisdiction">
                    <p>These Terms &amp; Conditions shall be governed by applicable laws of India.</p>
                    <p>Any disputes shall be subject to jurisdiction of competent courts in Telangana.</p>
                </Section>

                <Section title="Contact">
                    <div className="bg-white border border-rvs-navy-10 rounded-2xl p-6 md:p-8">
                        <p className="font-bold text-rvs-navy text-lg">RVS Electronics</p>
                        <p className="mt-2">{ADDRESS}</p>
                        <p className="mt-1"><span className="font-semibold text-rvs-navy">Phone / WhatsApp:</span> {PHONE_DISPLAY}</p>
                        <p className="mt-1"><span className="font-semibold text-rvs-navy">Email:</span> {EMAIL}</p>
                        <button
                            onClick={() => openWhatsApp("Hello RVS Electronics, I have a question about your Terms & Conditions.")}
                            data-testid="terms-whatsapp-btn"
                            className="mt-6 btn-orange inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold"
                        >
                            <MessageCircle className="w-4 h-4" /> Ask on WhatsApp
                        </button>
                    </div>
                </Section>
            </article>
        </div>
    );
}
